import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { GRID_BREAKPOINT_SMALL } from 'HubStyleTokens/sizes';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UICardGrid from 'UIComponents/card/UICardGrid';
const STACKED_LAYOUT = `
  'prev   contents  next'
  '.      steps     .   '
`;
const RESPONSIVE_LAYOUT = `
  'contents   contents    contents'
  'prev       steps       next    '
`;
const Dot = styled.span.withConfig({
  displayName: "Carousel__Dot",
  componentId: "nfajl4-0"
})(["cursor:pointer;height:15px;width:15px;margin:0 2px;border-radius:50%;display:inline-block;transition:background-color 0.6s ease;&:not(:last-child){margin-right:2%;}background-color:", ";"], props => props['aria-current'] ? `#7C98B6` : `#CBD6E2`);
const CarouselPrevious = styled(UIIconButton).withConfig({
  displayName: "Carousel__CarouselPrevious",
  componentId: "nfajl4-1"
})(["align-self:center;grid-area:prev;"]);
const CarouselNext = styled(UIIconButton).withConfig({
  displayName: "Carousel__CarouselNext",
  componentId: "nfajl4-2"
})(["align-self:center;grid-area:next;"]);
const CarouselWrapper = styled.section.withConfig({
  displayName: "Carousel__CarouselWrapper",
  componentId: "nfajl4-3"
})(["display:flex;flex-wrap:wrap;grid-column-gap:20px;grid-template-areas:", ";grid-template-columns:auto 1fr auto;padding:20px;padding-top:0;position:relative;> *{flex:1 1 100%;}@supports (display:grid){display:grid;}@media only screen and (max-width:", "){grid-template-areas:", ";grid-template-columns:auto 1fr auto;&& > *{margin-right:0;}}"], STACKED_LAYOUT, GRID_BREAKPOINT_SMALL, RESPONSIVE_LAYOUT);
export default function Carousel({
  cards,
  maxCardsPerPage = 1
}) {
  const [page, setPage] = useState(1);
  const offset = (page - 1) * maxCardsPerPage;
  const limit = useMemo(() => cards.length && cards.length <= maxCardsPerPage * page ? cards.length : offset + maxCardsPerPage, [cards.length, maxCardsPerPage, offset, page]);
  const currentCards = useMemo(() => {
    return cards.slice(offset, limit);
  }, [cards, offset, limit]);
  const pageCount = Math.ceil(cards.length / maxCardsPerPage);
  const needsPagination = pageCount > 1;
  const isScrolledToBeginning = page === 1;
  const isScrolledToEnd = page === pageCount;
  const handlePageChange = useCallback(newPage => {
    setPage(newPage);
  }, [setPage]);
  const renderedCarouselSteps = useMemo(() => {
    const steps = [];

    for (let currentPage = 0; currentPage < pageCount; currentPage++) {
      const isCurrentStep = page === currentPage + 1 || undefined;
      steps.push( /*#__PURE__*/_jsx(Dot, {
        className: "stepDot",
        "aria-current": isCurrentStep,
        onClick: () => handlePageChange(currentPage + 1)
      }, currentPage));
    }

    return steps;
  }, [handlePageChange, page, pageCount]);
  return /*#__PURE__*/_jsxs(CarouselWrapper, {
    children: [/*#__PURE__*/_jsx(UICardGrid, {
      columns: maxCardsPerPage,
      style: {
        gridArea: 'contents'
      },
      children: currentCards.map((item, index) => /*#__PURE__*/_jsx("div", {
        children: item
      }, index))
    }), needsPagination && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(CarouselPrevious, {
        disabled: isScrolledToBeginning,
        onClick: () => {
          if (page > 1) {
            handlePageChange(page - 1);
          }
        },
        size: "sm",
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: "left"
        })
      }), /*#__PURE__*/_jsx(CarouselNext, {
        disabled: isScrolledToEnd,
        onClick: () => {
          if (page < pageCount) {
            handlePageChange(page + 1);
          }
        },
        size: "sm",
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: "right"
        })
      })]
    }), needsPagination && /*#__PURE__*/_jsx("div", {
      style: {
        gridArea: 'steps',
        textAlign: 'center'
      },
      children: renderedCarouselSteps
    })]
  });
}